export type SalesOpportunityStage =
  | 'new'
  | 'qualification'
  | 'needs-confirmed'
  | 'offer-planned'
  | 'offer-presented'
  | 'follow-up'
  | 'won'
  | 'lost';

export type SalesSource =
  | 'phone'
  | 'email'
  | 'website'
  | 'referral'
  | 'marketplace'
  | 'existing-customer'
  | 'fair'
  | 'other';

export type SalesOfferChannel = 'phone' | 'email' | 'document' | 'onsite';
export type SalesOfferStatus = 'draft' | 'presented' | 'follow-up' | 'won' | 'lost' | 'expired';
export type SalesPositionKind = 'machine' | 'article' | 'service' | 'text';
export type SalesHandoffTarget = 'finance' | 'logistics';
export type SalesHandoffStatus = 'pending' | 'accepted' | 'completed' | 'failed';
export type SalesSearchStatus = 'all' | 'open' | 'won' | 'lost';

export interface SalesCustomerOption {
  readonly id: string;
  readonly number: string;
  readonly displayName: string;
  readonly city: string;
  readonly contactName: string;
  readonly email: string;
  readonly phone: string;
}

export interface SalesMachineOption {
  readonly id: string;
  readonly number: string;
  readonly manufacturer: string;
  readonly model: string;
  readonly serialNumber: string;
  readonly status: string;
  readonly customerId: string | null;
  /** Maschinentyp-Name (z. B. "Hochhubwagen") -- für die Anzeige im Picker. */
  readonly typeName: string;
  /** Technische Attribute (Schlüssel = Feldschlüssel, z. B. capacity/liftHeight/drive) für die Kriteriensuche. */
  readonly attributes: Readonly<Record<string, string>>;
  /** Verkaufspreis aus dem Maschinenstamm -- wird beim Auswählen automatisch in die Position gezogen. */
  readonly salesPrice: number;
  /** Einkaufspreis aus dem Maschinenstamm (für die Deckungsbeitrags-/Margenrechnung). */
  readonly purchasePrice: number;
}

export interface SalesMachineSpec {
  readonly label: string;
  readonly value: string;
  readonly unit: string;
}

export interface SalesMachineDetail {
  readonly id: string;
  readonly number: string;
  readonly manufacturer: string;
  readonly model: string;
  readonly serialNumber: string;
  readonly constructionYear: number | null;
  readonly typeName: string;
  /** Foto-URL aus dem Maschinenstamm (leer = kein Foto). Fürs Angebots-Beleg-Bild. */
  readonly imageUrl: string;
  /** Bemerkungen / Sonderausstattung aus dem Maschinenstamm. */
  readonly notes: string;
  /** Vollständige, beschriftete + sortierte Technik-Daten (aus den Maschinentyp-Feldern). */
  readonly specs: readonly SalesMachineSpec[];
}

export interface SalesItemOption {
  readonly id: string;
  readonly number: string;
  readonly kind: 'article' | 'service';
  readonly name: string;
  readonly unit: string;
  readonly salesPrice: number;
  readonly purchasePrice: number;
  readonly vatRate: number;
}

export interface SalesOpportunityRecord {
  readonly id: string;
  readonly tenantId: string;
  readonly number: string;
  readonly customerId: string | null;
  readonly customerNumber: string;
  readonly customerName: string;
  readonly prospectName: string;
  readonly contactName: string;
  readonly email: string;
  readonly phone: string;
  readonly title: string;
  readonly source: SalesSource;
  readonly stage: SalesOpportunityStage;
  readonly expectedValue: number;
  readonly probability: number;
  readonly weightedValue: number;
  readonly nextActionDate: string | null;
  readonly nextAction: string;
  readonly notes: string;
  readonly lostReason: string;
  readonly offerCount: number;
  readonly currentOfferId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface SalesOpportunitySearchInput {
  readonly tenantId: string;
  readonly query?: string;
  readonly stage?: SalesOpportunityStage | 'all';
  readonly page?: number;
  readonly pageSize?: number;
}

export interface NormalizedSalesOpportunitySearchInput {
  readonly tenantId: string;
  readonly query: string;
  readonly stage: SalesOpportunityStage | 'all';
  readonly page: number;
  readonly pageSize: number;
}

export interface SalesOpportunityPage {
  readonly items: readonly SalesOpportunityRecord[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface SaveSalesOpportunityInput {
  readonly tenantId: string;
  readonly opportunityId?: string;
  readonly customerId?: string | null;
  /** Business number + name of a customer picked from (or created in) the real customer base. */
  readonly customerNumber?: string;
  readonly customerName?: string;
  readonly prospectName?: string;
  readonly contactName?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly title: string;
  readonly source?: SalesSource;
  readonly stage?: SalesOpportunityStage;
  readonly expectedValue?: number;
  readonly probability?: number;
  readonly nextActionDate?: string | null;
  readonly nextAction?: string;
  readonly notes?: string;
}

export interface SalesOfferPositionRecord {
  readonly id: string;
  readonly positionNumber: number;
  readonly kind: SalesPositionKind;
  readonly sourceId: string | null;
  readonly sourceNumber: string;
  readonly machineId: string | null;
  readonly description: string;
  readonly quantity: number;
  readonly unit: string;
  readonly unitPrice: number;
  readonly purchasePrice: number;
  readonly discountPercent: number;
  readonly netAmount: number;
  readonly contributionMargin: number;
  readonly vatRate: number;
  /** Warengruppe der Position (Stufe 3) — schon beim Angebot/Auftrag setzbar, läuft bis in die Rechnung. */
  readonly warengruppeId: string | null;
}

export interface SalesOfferRecord {
  readonly id: string;
  readonly tenantId: string;
  readonly number: string;
  readonly opportunityId: string | null;
  readonly opportunityNumber: string;
  readonly customerId: string | null;
  readonly customerNumber: string;
  readonly customerName: string;
  readonly prospectName: string;
  readonly title: string;
  readonly channel: SalesOfferChannel;
  readonly status: SalesOfferStatus;
  readonly validUntil: string | null;
  readonly notes: string;
  readonly currency: 'EUR';
  readonly subtotal: number;
  readonly discountTotal: number;
  readonly netTotal: number;
  readonly vatTotal: number;
  readonly grossTotal: number;
  readonly contributionMargin: number;
  readonly positions: readonly SalesOfferPositionRecord[];
  readonly decisionReason: string;
  readonly decidedAt: string | null;
  readonly salesOrderId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface SalesOfferPositionInput {
  readonly kind: SalesPositionKind;
  readonly sourceId?: string | null;
  readonly sourceNumber?: string;
  readonly machineId?: string | null;
  readonly description: string;
  readonly quantity?: number;
  readonly unit?: string;
  readonly unitPrice?: number;
  readonly purchasePrice?: number;
  readonly discountPercent?: number;
  readonly vatRate?: number;
  readonly warengruppeId?: string | null;
}

export interface SaveSalesOfferInput {
  readonly tenantId: string;
  readonly offerId?: string;
  readonly opportunityId?: string | null;
  readonly customerId?: string | null;
  /** Business number + name of a customer picked from (or created in) the real customer base. */
  readonly customerNumber?: string;
  readonly customerName?: string;
  readonly prospectName?: string;
  readonly title: string;
  readonly channel?: SalesOfferChannel;
  readonly status?: Exclude<SalesOfferStatus, 'won' | 'lost'>;
  readonly validUntil?: string | null;
  readonly notes?: string;
  readonly positions: readonly SalesOfferPositionInput[];
}

export interface SalesOfferSearchInput {
  readonly tenantId: string;
  readonly query?: string;
  readonly status?: SalesOfferStatus | 'all';
  readonly page?: number;
  readonly pageSize?: number;
}

export interface NormalizedSalesOfferSearchInput {
  readonly tenantId: string;
  readonly query: string;
  readonly status: SalesOfferStatus | 'all';
  readonly page: number;
  readonly pageSize: number;
}

export interface SalesOfferPage {
  readonly items: readonly SalesOfferRecord[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface DecideSalesOfferInput {
  readonly tenantId: string;
  readonly offerId: string;
  readonly decision: 'won' | 'lost';
  readonly reason?: string;
}

export interface SalesHandoffRecord {
  readonly id: string;
  readonly tenantId: string;
  readonly salesOrderId: string;
  readonly salesOrderNumber: string;
  readonly target: SalesHandoffTarget;
  readonly status: SalesHandoffStatus;
  readonly title: string;
  readonly payload: Readonly<Record<string, unknown>>;
  readonly errorMessage: string;
  /** Number of the artefact the handoff produced once executed (e.g. RS-26-… invoice draft). */
  readonly resultNumber: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CompleteSalesHandoffInput {
  readonly tenantId: string;
  readonly handoffId: string;
  /** Number of the produced artefact (e.g. the Finance invoice-draft number). */
  readonly resultNumber?: string;
}

export interface CreateSalesDeliveryInput {
  readonly tenantId: string;
  readonly orderId: string;
}

/** Verkaufsmeldung: Auslieferungs-/Abrechnungsdetails, die der Verkäufer nach dem Gewinnen einträgt. */
export interface SalesOrderDelivery {
  /** Lieferadresse (mehrzeilig). */
  readonly address: string;
  /** Wunsch-/Fertigstellungstermin (ISO-Datum). */
  readonly readyDate: string;
  /** Liefertermin (ISO-Datum). */
  readonly deliveryDate: string;
  /** Lieferart (z. B. Anlieferung, Selbstabholung, Spedition). */
  readonly method: string;
  /** Lieferkosten (netto) -- werden als Position in die Rechnung übernommen. */
  readonly cost: number;
  /** Preis-/Kurs-Hinweis für Finance. */
  readonly priceNote: string;
}

export interface SaveSalesOrderDeliveryInput {
  readonly tenantId: string;
  readonly orderId: string;
  readonly delivery: SalesOrderDelivery;
}

export interface SalesOrderRecord {
  readonly id: string;
  readonly tenantId: string;
  readonly number: string;
  readonly businessCaseNumber: string;
  readonly offerId: string;
  readonly offerNumber: string;
  readonly opportunityId: string | null;
  readonly customerId: string | null;
  readonly customerNumber: string;
  readonly customerName: string;
  readonly prospectName: string;
  readonly title: string;
  readonly status: 'created' | 'delivery-planning' | 'delivered' | 'billing-ready' | 'billed' | 'cancelled';
  readonly netTotal: number;
  readonly vatTotal: number;
  readonly grossTotal: number;
  /** Verkaufsmeldung (Auslieferdetails); null, solange der Verkäufer sie nicht eingetragen hat. */
  readonly delivery: SalesOrderDelivery | null;
  readonly positions: readonly SalesOfferPositionRecord[];
  readonly handoffs: readonly SalesHandoffRecord[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface SalesOrderSearchInput {
  readonly tenantId: string;
  readonly query?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

export interface NormalizedSalesOrderSearchInput {
  readonly tenantId: string;
  readonly query: string;
  readonly page: number;
  readonly pageSize: number;
}

export interface SalesOrderPage {
  readonly items: readonly SalesOrderRecord[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface SalesOverview {
  readonly openOpportunities: number;
  readonly pipelineValue: number;
  readonly weightedPipelineValue: number;
  readonly followUpsDue: number;
  readonly openOffers: number;
  readonly wonThisMonth: number;
  readonly wonValueThisMonth: number;
  readonly pendingHandoffs: number;
}

export interface SalesGateway {
  getOverview(tenantId: string): Promise<SalesOverview>;
  searchCustomerOptions(tenantId: string, query?: string): Promise<readonly SalesCustomerOption[]>;
  searchMachineOptions(tenantId: string, query?: string): Promise<readonly SalesMachineOption[]>;
  /** Volle Maschinendaten (Technik + Foto) für den Angebots-Beleg -- auch für bereits verkaufte Maschinen. */
  getMachineDetail(tenantId: string, machineId: string): Promise<SalesMachineDetail | null>;
  searchItemOptions(tenantId: string, query?: string): Promise<readonly SalesItemOption[]>;
  searchOpportunities(input: SalesOpportunitySearchInput): Promise<SalesOpportunityPage>;
  getOpportunity(tenantId: string, opportunityId: string): Promise<SalesOpportunityRecord>;
  saveOpportunity(input: SaveSalesOpportunityInput): Promise<SalesOpportunityRecord>;
  searchOffers(input: SalesOfferSearchInput): Promise<SalesOfferPage>;
  getOffer(tenantId: string, offerId: string): Promise<SalesOfferRecord>;
  saveOffer(input: SaveSalesOfferInput): Promise<SalesOfferRecord>;
  decideOffer(input: DecideSalesOfferInput): Promise<SalesOrderRecord | null>;
  searchOrders(input: SalesOrderSearchInput): Promise<SalesOrderPage>;
  getOrder(tenantId: string, orderId: string): Promise<SalesOrderRecord>;
  listHandoffs(tenantId: string, limit?: number): Promise<readonly SalesHandoffRecord[]>;
  /** Marks a handoff completed once its target module executed it (e.g. Finance created the draft). */
  completeHandoff(input: CompleteSalesHandoffInput): Promise<SalesHandoffRecord>;
  /**
   * Bereitet die Auslieferung vor (Verwaltung): zieht eine LS-Nummer aus der gemeinsamen Lieferschein-
   * Serie und setzt die Logistik-Übergabe auf 'accepted' -- Lieferschein + UVV-Prüfbuch sind damit
   * erstellt, der Auftrag wartet auf die Werkstatt-Durchsicht. Liefert den aktualisierten Auftrag.
   */
  createDelivery(input: CreateSalesDeliveryInput): Promise<SalesOrderRecord>;
  /**
   * Bestätigt die Auslieferungsdurchsicht der Werkstatt: schließt die Logistik-Übergabe ab und setzt
   * den Auftrag auf 'delivered'. Setzt voraus, dass die Belege vorbereitet sind ('accepted').
   */
  confirmDelivery(input: CreateSalesDeliveryInput): Promise<SalesOrderRecord>;
  /** Speichert die Verkaufsmeldung (Auslieferdetails) am Auftrag. */
  setDeliveryDetails(input: SaveSalesOrderDeliveryInput): Promise<SalesOrderRecord>;
}

const sources = new Set<SalesSource>([
  'phone', 'email', 'website', 'referral', 'marketplace', 'existing-customer', 'fair', 'other',
]);
const opportunityStages = new Set<SalesOpportunityStage>([
  'new', 'qualification', 'needs-confirmed', 'offer-planned', 'offer-presented', 'follow-up', 'won', 'lost',
]);
const offerChannels = new Set<SalesOfferChannel>(['phone', 'email', 'document', 'onsite']);
const offerStatuses = new Set<Exclude<SalesOfferStatus, 'won' | 'lost'>>(['draft', 'presented', 'follow-up', 'expired']);
const positionKinds = new Set<SalesPositionKind>(['machine', 'article', 'service', 'text']);

function nonNegative(value: number | undefined, label: string): number {
  const normalized = value ?? 0;
  if (!Number.isFinite(normalized) || normalized < 0) throw new Error(`${label} darf nicht negativ sein.`);
  return Math.round(normalized * 100) / 100;
}

export function normalizeSalesOpportunitySearchInput(
  input: SalesOpportunitySearchInput,
): NormalizedSalesOpportunitySearchInput {
  return {
    tenantId: input.tenantId,
    query: input.query?.trim() ?? '',
    stage: input.stage ?? 'all',
    page: Math.max(1, Math.trunc(input.page ?? 1)),
    pageSize: Math.min(100, Math.max(1, Math.trunc(input.pageSize ?? 25))),
  };
}

export function normalizeSalesOfferSearchInput(input: SalesOfferSearchInput): NormalizedSalesOfferSearchInput {
  return {
    tenantId: input.tenantId,
    query: input.query?.trim() ?? '',
    status: input.status ?? 'all',
    page: Math.max(1, Math.trunc(input.page ?? 1)),
    pageSize: Math.min(100, Math.max(1, Math.trunc(input.pageSize ?? 25))),
  };
}

export function normalizeSalesOrderSearchInput(input: SalesOrderSearchInput): NormalizedSalesOrderSearchInput {
  return {
    tenantId: input.tenantId,
    query: input.query?.trim() ?? '',
    page: Math.max(1, Math.trunc(input.page ?? 1)),
    pageSize: Math.min(100, Math.max(1, Math.trunc(input.pageSize ?? 25))),
  };
}

export function normalizeSalesOpportunityInput(input: SaveSalesOpportunityInput): SaveSalesOpportunityInput {
  const title = input.title.trim();
  const prospectName = input.prospectName?.trim() ?? '';
  if (title.length < 3) throw new Error('Die Verkaufschance benötigt einen aussagekräftigen Titel.');
  if (!input.customerId && !input.customerNumber && !prospectName) throw new Error('Bitte einen Kunden oder Interessenten angeben.');
  const source = input.source ?? 'other';
  const stage = input.stage ?? 'new';
  if (!sources.has(source)) throw new Error('Die Quelle der Verkaufschance ist ungültig.');
  if (!opportunityStages.has(stage)) throw new Error('Der Status der Verkaufschance ist ungültig.');
  const probability = Math.trunc(input.probability ?? 0);
  if (probability < 0 || probability > 100) throw new Error('Die Wahrscheinlichkeit muss zwischen 0 und 100 liegen.');
  return {
    tenantId: input.tenantId,
    ...(input.opportunityId ? { opportunityId: input.opportunityId } : {}),
    customerId: input.customerId ?? null,
    customerNumber: input.customerNumber?.trim() ?? '',
    customerName: input.customerName?.trim() ?? '',
    prospectName,
    contactName: input.contactName?.trim() ?? '',
    email: input.email?.trim().toLowerCase() ?? '',
    phone: input.phone?.trim() ?? '',
    title,
    source,
    stage,
    expectedValue: nonNegative(input.expectedValue, 'Der erwartete Auftragswert'),
    probability,
    nextActionDate: input.nextActionDate || null,
    nextAction: input.nextAction?.trim() ?? '',
    notes: input.notes?.trim() ?? '',
  };
}

export function normalizeSalesOfferInput(input: SaveSalesOfferInput): SaveSalesOfferInput {
  const title = input.title.trim();
  const prospectName = input.prospectName?.trim() ?? '';
  if (title.length < 3) throw new Error('Das Angebot benötigt einen aussagekräftigen Titel.');
  if (!input.customerId && !input.customerNumber && !prospectName) throw new Error('Bitte einen Kunden oder Interessenten angeben.');
  const channel = input.channel ?? 'document';
  const status = input.status ?? 'draft';
  if (!offerChannels.has(channel)) throw new Error('Der Angebotskanal ist ungültig.');
  if (!offerStatuses.has(status)) throw new Error('Der Angebotsstatus ist ungültig.');
  if (input.positions.length === 0) throw new Error('Das Angebot benötigt mindestens eine Position.');
  const positions = input.positions.map((position): SalesOfferPositionInput => {
    if (!positionKinds.has(position.kind)) throw new Error('Eine Angebotsposition besitzt eine ungültige Art.');
    const description = position.description.trim();
    if (!description) throw new Error('Jede Angebotsposition benötigt eine Beschreibung.');
    const quantity = position.quantity ?? 1;
    if (!Number.isFinite(quantity) || quantity <= 0) throw new Error('Die Positionsmenge muss größer als null sein.');
    const discountPercent = position.discountPercent ?? 0;
    if (!Number.isFinite(discountPercent) || discountPercent < 0 || discountPercent > 100) {
      throw new Error('Der Positionsrabatt muss zwischen 0 und 100 Prozent liegen.');
    }
    const vatRate = position.vatRate ?? 19;
    if (!Number.isFinite(vatRate) || vatRate < 0 || vatRate > 100) {
      throw new Error('Der Umsatzsteuersatz muss zwischen 0 und 100 Prozent liegen.');
    }
    return {
      kind: position.kind,
      sourceId: position.sourceId ?? null,
      sourceNumber: position.sourceNumber?.trim() ?? '',
      machineId: position.machineId ?? null,
      description,
      quantity: Math.round(quantity * 1000) / 1000,
      unit: position.unit?.trim() || 'Stück',
      unitPrice: nonNegative(position.unitPrice, 'Der Verkaufspreis'),
      purchasePrice: nonNegative(position.purchasePrice, 'Der Einkaufspreis'),
      discountPercent: Math.round(discountPercent * 100) / 100,
      vatRate: Math.round(vatRate * 100) / 100,
      warengruppeId: position.warengruppeId ?? null,
    };
  });
  return {
    tenantId: input.tenantId,
    ...(input.offerId ? { offerId: input.offerId } : {}),
    opportunityId: input.opportunityId ?? null,
    customerId: input.customerId ?? null,
    customerNumber: input.customerNumber?.trim() ?? '',
    customerName: input.customerName?.trim() ?? '',
    prospectName,
    title,
    channel,
    status,
    validUntil: input.validUntil || null,
    notes: input.notes?.trim() ?? '',
    positions,
  };
}

export function calculateSalesOfferPosition(
  input: SalesOfferPositionInput,
  positionNumber: number,
  id = `position-${positionNumber}`,
): SalesOfferPositionRecord {
  const quantity = input.quantity ?? 1;
  const unitPrice = input.unitPrice ?? 0;
  const purchasePrice = input.purchasePrice ?? 0;
  const discountPercent = input.discountPercent ?? 0;
  const grossLineBeforeDiscount = quantity * unitPrice;
  const netAmount = Math.round(grossLineBeforeDiscount * (1 - discountPercent / 100) * 100) / 100;
  const contributionMargin = Math.round((netAmount - quantity * purchasePrice) * 100) / 100;
  return {
    id,
    positionNumber,
    kind: input.kind,
    sourceId: input.sourceId ?? null,
    sourceNumber: input.sourceNumber?.trim() ?? '',
    machineId: input.machineId ?? null,
    description: input.description.trim(),
    quantity,
    unit: input.unit?.trim() || 'Stück',
    unitPrice,
    purchasePrice,
    discountPercent,
    netAmount,
    contributionMargin,
    vatRate: input.vatRate ?? 19,
    warengruppeId: input.warengruppeId ?? null,
  };
}

export function calculateSalesOfferTotals(positions: readonly SalesOfferPositionRecord[]): Pick<
  SalesOfferRecord,
  'subtotal' | 'discountTotal' | 'netTotal' | 'vatTotal' | 'grossTotal' | 'contributionMargin'
> {
  const subtotal = positions.reduce((sum, position) => sum + position.quantity * position.unitPrice, 0);
  const netTotal = positions.reduce((sum, position) => sum + position.netAmount, 0);
  const vatTotal = positions.reduce((sum, position) => sum + position.netAmount * position.vatRate / 100, 0);
  const contributionMargin = positions.reduce((sum, position) => sum + position.contributionMargin, 0);
  return {
    subtotal: Math.round(subtotal * 100) / 100,
    discountTotal: Math.round((subtotal - netTotal) * 100) / 100,
    netTotal: Math.round(netTotal * 100) / 100,
    vatTotal: Math.round(vatTotal * 100) / 100,
    grossTotal: Math.round((netTotal + vatTotal) * 100) / 100,
    contributionMargin: Math.round(contributionMargin * 100) / 100,
  };
}
